import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, List, ListItem, ListItemText, ListItemSecondaryAction,
  IconButton, Chip, Alert, LinearProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Tooltip,
} from '@mui/material';
import { Delete, CloudUpload, Sync, Description, WarningAmber } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useCapabilities } from '../contexts/CapabilitiesContext';

const API = process.env.REACT_APP_API_ENDPOINT || '';

/** Formats Bedrock can actually parse — must stay in step with kb_admin.ALLOWED_EXT. */
const ACCEPT = '.pdf,.txt,.md,.html,.doc,.docx,.csv,.xls,.xlsx';

interface KbDoc {
  filename: string;
  size: number;
  lastModified: string;
  empty: boolean;
}

interface SyncState {
  status: string;
  documentsIndexed?: number | null;
  documentsDeleted?: number | null;
  documentsFailed?: number | null;
  updatedAt?: string;
}

const human = (b: number) => (b === 0 ? '0 B' : b < 1024 ? `${b} B`
  : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

/**
 * Admin management of this silo's own standards library (the CAL_API_KB knowledge base).
 *
 * Trindent's BaseKnowledge is deliberately NOT reachable from here — it ships with every silo and a
 * customer admin must not be able to delete it.
 *
 * Adding or removing a file changes storage only; Cal keeps answering from the existing index until an
 * ingestion runs. A background job picks changes up within a few minutes, and Sync forces it now. The
 * UI says so explicitly rather than implying a delete takes effect immediately.
 */
const KnowledgeBaseSettings: React.FC = () => {
  const { getIdTokenClaims } = useAuth0();
  const { can } = useCapabilities();
  const isAdmin = can('edit_settings');

  const [docs, setDocs] = useState<KbDoc[]>([]);
  const [sync, setSync] = useState<SyncState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<KbDoc | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const token = useCallback(async () => (await getIdTokenClaims())?.__raw, [getIdTokenClaims]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/knowledge-base/documents`, {
        headers: { Authorization: `Bearer ${await token()}` },
      });
      if (!res.ok) throw new Error(`Could not load documents (${res.status})`);
      const data = await res.json();
      setDocs(data.documents || []);
      setSync(data.sync || null);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // While an ingestion is running, poll so the admin sees it finish rather than guessing.
  useEffect(() => {
    if (!sync || !['STARTING', 'IN_PROGRESS'].includes(sync.status)) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [sync, load]);

  const upload = async (file: File) => {
    setBusy(`Uploading ${file.name}…`);
    setError(null);
    try {
      const res = await fetch(`${API}/knowledge-base/upload-url`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, size: file.size, contentType: file.type }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Upload refused (${res.status})`);
      const put = await fetch(data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      setNotice(`${file.name} added. Cal picks it up on the next sync.`);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const remove = async (doc: KbDoc) => {
    setBusy(`Removing ${doc.filename}…`);
    setError(null);
    try {
      const res = await fetch(
        `${API}/knowledge-base/documents?filename=${encodeURIComponent(doc.filename)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${await token()}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Delete failed (${res.status})`);
      setNotice(data.message || 'Removed. Run Sync to take it out of Cal’s knowledge.');
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
      setPendingDelete(null);
    }
  };

  const startSync = async () => {
    setBusy('Starting sync…');
    setError(null);
    try {
      const res = await fetch(`${API}/knowledge-base/sync`, {
        method: 'POST', headers: { Authorization: `Bearer ${await token()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Sync failed (${res.status})`);
      setNotice(data.message);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const running = !!sync && ['STARTING', 'IN_PROGRESS'].includes(sync.status);
  const emptyCount = docs.filter(d => d.empty).length;

  if (!isAdmin) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Managing Cal’s reference documents requires administrator access.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Cal’s reference documents</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Standards and reference material Cal can cite when answering questions for your site. These are
        private to your site. Cal’s built-in product knowledge is separate and always available.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {emptyCount > 0 && (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 1 }}>
          {emptyCount} document{emptyCount > 1 ? 's are' : ' is'} empty, so Cal cannot read
          {emptyCount > 1 ? ' them' : ' it'}. Re-upload or remove.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <input ref={fileInput} type="file" accept={ACCEPT} hidden
               onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
        <Button size="small" variant="contained" startIcon={<CloudUpload />}
                disabled={!!busy} onClick={() => fileInput.current?.click()}>
          Add document
        </Button>
        <Tooltip title="Update Cal's knowledge with the documents listed below. This also happens automatically within a few minutes of a change.">
          <span>
            <Button size="small" variant="outlined" startIcon={<Sync />}
                    disabled={!!busy || running} onClick={startSync}>
              {running ? 'Syncing…' : 'Sync now'}
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {sync && (
          <Chip size="small"
                color={running ? 'info' : sync.documentsFailed ? 'warning' : 'default'}
                label={
                  running ? 'Sync running'
                  : sync.status === 'NEVER_RUN' ? 'Never synced'
                  : sync.documentsFailed
                    ? `Last sync: ${sync.documentsFailed} failed`
                    : 'Up to date'
                } />
        )}
      </Box>

      {(loading || busy || running) && <LinearProgress sx={{ mb: 1 }} />}
      {busy && <Typography variant="caption" color="text.secondary">{busy}</Typography>}

      {!loading && docs.length === 0 && (
        <Alert severity="info">
          No documents yet. Add the standards your team works to and Cal will cite them.
        </Alert>
      )}

      <List dense>
        {docs.map(d => (
          <ListItem key={d.filename} divider>
            <Description fontSize="small" color={d.empty ? 'warning' : 'action'} sx={{ mr: 1 }} />
            <ListItemText
              primary={d.filename}
              secondary={`${human(d.size)}${d.empty ? ' — empty, Cal cannot read this' : ''} · added ${new Date(d.lastModified).toLocaleDateString()}`}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }} />
            <ListItemSecondaryAction>
              <IconButton edge="end" size="small" disabled={!!busy}
                          onClick={() => setPendingDelete(d)}>
                <Delete fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} sx={{ zIndex: 2200 }}>
        <DialogTitle>Remove this document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{pendingDelete?.filename}</strong> will be removed for everyone at your site.
            Cal stops citing it once the next sync finishes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!!busy}
                  onClick={() => pendingDelete && remove(pendingDelete)}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnowledgeBaseSettings;
